import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { ICohortSampleOperationFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import { builderSetCohortSampleOperation } from '../../../../redux/slices/oldBuilder';
import { ROWS_COUNT_ALLOWS_COMPARE_OR } from '../../../../config/constants';
import { builderSampledCohortSelector } from '../../../../redux/selectors/builder';
import styled from 'styled-components';
import {
  ConnectorContainer,
  ConnectorWrapper,
} from '../../../../components/Builder/BuilderUI';
import {
  SingleSelect,
  SingleSelectLabel,
  SingleSelectOption,
} from '../../../../components/Dropdown/SingleSelect';

interface IProps {
  autoFocus: boolean;
}

const StyledMenuWrapper = styled(ConnectorWrapper)`
  &:hover {
    cursor: pointer;
  }
`;

/**
 * This component is only used in Cohort Sample operator.
 * User is able to change the operator(OR, COMPARE) if there are only two cohort rows,
 * otherwise, always use AND operator.
 */
export default ({ autoFocus }: IProps) => {
  const dispatch = useAppDispatch();

  const builderSampledCohort = useAppSelector(builderSampledCohortSelector);
  const { sampleOperation, operations, defaultOperation } =
    builderSampledCohort;

  const builderCohorts = useAppSelector(
    ({ builder }) => builder.builderCohorts,
  );

  const handleChange = (newValue: ICohortSampleOperationFieldsFragment) => {
    dispatch(builderSetCohortSampleOperation({ value: newValue }));
  };

  const selectedOption: string | null = useMemo(() => {
    if (!sampleOperation && defaultOperation) {
      return defaultOperation.key;
    }

    if (sampleOperation) {
      return sampleOperation.key;
    }

    return null;
  }, [defaultOperation, sampleOperation]);

  // if row.length is not 2 - use default operation
  if (
    defaultOperation &&
    builderCohorts.length !== ROWS_COUNT_ALLOWS_COMPARE_OR
  ) {
    return (
      <ConnectorContainer>
        <ConnectorWrapper>{defaultOperation.key}</ConnectorWrapper>
      </ConnectorContainer>
    );
  }

  return (
    <ConnectorContainer>
      <SingleSelect
        menuMinWidth={100}
        isDefaultOpen={autoFocus}
        placement="bottom-start"
        trigger={
          <StyledMenuWrapper>
            <SingleSelectLabel>{selectedOption}</SingleSelectLabel>
          </StyledMenuWrapper>
        }
      >
        {operations.map((operation) => (
          <SingleSelectOption
            key={operation.key}
            label={operation.key}
            onClick={() => handleChange(operation)}
          />
        ))}
      </SingleSelect>
    </ConnectorContainer>
  );
};
